import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import SiteVisitFieldNotes from '@/components/SiteVisitFieldNotes';
import { useAuth } from '@/context/AuthContext';
import { apiHelpers } from '@/lib/api';
import { getFieldNotesDraft, clearFieldNotesDraft } from '@/lib/offlineSync';
import { useFieldNotesSave, SAVE_STATUS } from '@/hooks/useFieldNotesSave';
import { useDeveloperMode } from '@/hooks/useDeveloperMode';

/**
 * Full-page Field Notes — replaces the side-drawer modal so crew can fill out
 * notes on a real page (better on mobile, no cramped feeling).
 */
const FieldNotesPage = () => {
  const { visitId } = useParams();
  const navigate = useNavigate();
  const { organization, user } = useAuth();
  const [visit, setVisit] = useState(null);
  const [fieldNotesData, setFieldNotesData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const saveHook = useFieldNotesSave({ organization, user, visitId });
  const developerMode = useDeveloperMode();

  // Load visit + any offline draft
  useEffect(() => {
    const load = async () => {
      if (!organization?.id || !visitId) return;
      try {
        const res = await apiHelpers.getSiteVisit(organization.id, visitId);
        const v = res.data;
        setVisit(v);

        // Hydrate from offline draft first (most recent local edits), fallback to server.
        // CRITICAL: ignore stale local drafts when the server is newer — this is the
        // cross-device sync fix. Without this, Device B's old local draft masks
        // Device A's freshly-synced server data forever.
        let draft = null;
        try {
          draft = await getFieldNotesDraft(visitId);
        } catch {
          /* IndexedDB unavailable, ignore */
        }
        // Server "freshness" timestamp: last_edited_at (set by backend on every PUT),
        // falling back to updated_at if older versions of the schema are in play.
        const serverTs = v.last_edited_at || v.updated_at || null;
        const draftTs = draft?.updated_at || null;
        let useDraft = !!draft;

        // SolidHome safety rule: if the server doc is EFFECTIVELY EMPTY (no
        // notes, no measurements, no photos) but the local draft has real
        // content, ALWAYS prefer the draft — regardless of timestamps.
        // This is how we recover an iPad's unsynced session even when an
        // unrelated server-side update bumped updated_at past the draft.
        const serverEffectivelyEmpty = !v.internal_notes && !v.project_notes
          && (!v.structured_measurements || v.structured_measurements.length === 0)
          && (!v.photos || v.photos.length === 0);
        const draftHasContent = draft?.notes && (
          (draft.notes.internalNotes && draft.notes.internalNotes.trim()) ||
          (draft.notes.clientNotes && draft.notes.clientNotes.trim()) ||
          (Array.isArray(draft.notes.measurements) && draft.notes.measurements.length > 0) ||
          (Array.isArray(draft.notes.photos) && draft.notes.photos.length > 0)
        );

        if (useDraft && serverTs && draftTs && !(serverEffectivelyEmpty && draftHasContent)) {
          try {
            if (new Date(serverTs).getTime() > new Date(draftTs).getTime()) {
              useDraft = false;
              // CRITICAL: We do NOT delete the local draft here. Previously
              // we wiped it immediately ("server has newer data"), which is
              // exactly how SolidHome lost 9 photos + measurements: the
              // backend's updated_at was bumped by something unrelated (a
              // status flip or completion snapshot) while their iPad had
              // a pending queue item, and the next page load nuked their
              // local-only work without warning.
              //
              // New rule: hydrate from server (since it IS newer), but
              // preserve the draft as a read-only safety net. The user
              // can still recover via Developer Mode → Sync Queue if it
              // matters. Drafts only get cleared after an explicit
              // successful manual save (see useFieldNotesSave.save).
              console.log('[FieldNotes] Server is newer than local draft — hydrating from server, draft kept as backup', { serverTs, draftTs });
            }
          } catch {
            // Date parsing failed — fall back to draft (safer than losing local edits)
          }
        } else if (serverEffectivelyEmpty && draftHasContent) {
          console.warn('[FieldNotes] Server is empty but draft has content — recovering from local draft', { draftTs });
          toast.success('Restored unsaved work from this device', {
            description: 'Tap Save to push it to the cloud.',
            duration: 8000,
          });
        }
        const draftNotes = useDraft && draft && draft.notes && typeof draft.notes === 'object' ? draft.notes : null;

        const merged = {
          internalNotes: draftNotes?.internalNotes ?? v.internal_notes ?? '',
          clientNotes: draftNotes?.clientNotes ?? v.project_notes ?? '',
          clientExpectations: draftNotes?.clientExpectations ?? v.client_expectations ?? {
            wantsDone: '',
            timeline: '',
            budgetRange: '',
            specialRequests: '',
          },
          measurements:
            draftNotes?.measurements ??
            v.structured_measurements ??
            (v.measurements
              ? Object.entries(v.measurements)
                  .filter(([, val]) => val)
                  .map(([k, val]) => ({ id: k, label: k, value: val, unit: '' }))
              : []),
          photos: draftNotes?.photos ?? v.photos ?? [],
          sketches: draftNotes?.sketches ?? v.sketches ?? [],
          siteConditions: draftNotes?.siteConditions ?? v.site_conditions ?? [],
          customConditions: draftNotes?.customConditions ?? v.custom_conditions ?? [],
          // Backend stores `checklist` (NOT checklist_items)
          checklist: draftNotes?.checklist ?? v.checklist ?? [],
          isChecklistComplete: draftNotes?.isChecklistComplete ?? v.is_checklist_complete ?? false,
          internalVoiceRecordings: draftNotes?.internalVoiceRecordings ?? v.internal_voice_recordings ?? [],
          clientVoiceRecordings: draftNotes?.clientVoiceRecordings ?? v.client_voice_recordings ?? [],
        };
        setFieldNotesData(merged);
      } catch (err) {
        console.error('Failed to load site visit:', err);
        toast.error('Could not load site visit');
        navigate('/site-visits');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [organization?.id, visitId, navigate]);

  const handleAutoSave = async (data) => {
    await saveHook.autoSave(data);
  };

  const handleSave = async (data, opts = {}) => {
    const result = await saveHook.save(data, { mode: 'manual' });
    if (result?.ok && !opts.skipClose) {
      navigate(`/site-visits/${visitId}`);
    }
    if (!result?.ok) {
      // Failure path: hook already toasted + set status; keep user on the page
      // so they can see the badge and tap Retry now. Don't auto-navigate away
      // when the save didn't reach the server.
      const err = new Error('save_failed');
      err.classified = result?.classified;
      throw err;
    }
    return result;
  };

  const handleMarkComplete = async () => {
    if (!organization?.id || !visitId) return;
    try {
      const res = await apiHelpers.completeSiteVisit(
        organization.id,
        visitId,
        user.id,
        user.name,
        false,
      );
      const version = res?.data?.version;
      toast.success(
        version && version > 1
          ? `Re-snapshotted (v${version}) — PDFs use the original snapshot`
          : 'Site visit complete — snapshot saved',
      );
      // Reload the visit so the Completed banner & status sync.
      try {
        const fresh = await apiHelpers.getSiteVisit(organization.id, visitId);
        setVisit(fresh.data);
      } catch { /* ignore */ }
      navigate(`/site-visits/${visitId}`);
    } catch (err) {
      console.error('Failed to mark complete:', err);
      toast.error(err?.response?.data?.detail || 'Failed to mark site visit complete');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <Loader2 className="w-6 h-6 animate-spin" />
          <p className="text-sm">Loading field notes…</p>
        </div>
      </div>
    );
  }

  if (!visit) return null;

  return (
    <SiteVisitFieldNotes
      mode="page"
      isOpen={true}
      onClose={() => navigate(`/site-visits/${visitId}`)}
      onSave={handleSave}
      onAutoSave={handleAutoSave}
      initialData={fieldNotesData}
      serviceType={visit.service_type || 'General'}
      businessType={organization?.business_type || organization?.industry || null}
      secondaryNiches={organization?.secondary_niches || []}
      siteVisitId={visitId}
      orgId={organization?.id}
      saveStatus={saveHook.status}
      saveLastSavedAt={saveHook.lastSavedAt}
      saveLastError={saveHook.lastError}
      onRetrySave={async () => { await saveHook.retry(fieldNotesData); }}
      developerMode={developerMode}
      onMarkComplete={handleMarkComplete}
      isCompleted={visit?.status === 'completed'}
    />
  );
};

export default FieldNotesPage;
